import React, { useEffect, useState } from 'react';
import ModalProfilMembre from '../../Publications/components/DetailCandidature/ModalProfilMembre';
import type { ProfileData } from '../../../services/profileService';
import Button from '../../../composants/Button/Button';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import { CommandeAnalyse } from '../domain/analyse/types';
import './OperationConsultationSuivi.css';

// Contact (interlocuteur) rattache a un titulaire de l'annuaire ou a une commande.
interface ContactInfo {
  nom?: string;
  prenom?: string;
  email?: string;
  email_contact?: string;
  telephone?: string;
  fonction?: string;
}

// Titulaire renvoye par GET /prestataire/annuaire (annuaire des contrats du lot/departement).
interface Titulaire {
  id: number | string;
  rang?: number;
  numero?: string | null;
  entite: { id: number; nom: string; logo_url?: string | null; siret?: string | null };
  lot_technique?: { id: number; nom: string; code: string; image_url?: string | null };
  prestataire?: ContactInfo | null;
}

// Forme de la modale (sous-ensemble de contactInfo de ModalProfilMembre).
interface ModalContact {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
  contratNumero: string | null;
}

// Carte d'affichage : une commande (candidature) enrichie de son entree d'annuaire.
interface ConsultationCard {
  key: number | string;
  entite: { id?: number | string; nom?: string; siret?: string | null; logo_url?: string | null };
  lotTechnique?: { id: number; nom: string; code: string; image_url?: string | null };
  contact: ContactInfo | null;
  cmd?: CommandeAnalyse;
}

// Deposant de l'offre (prestataire signataire de la commande), resolu par core.
interface Deposant {
  nom?: string;
  prenom?: string;
  fonction?: string;
}

interface OperationConsultationSuiviProps {
  commandes: CommandeAnalyse[];
  lotTechnique?: number | string;
  departement?: number | string;
  // IP du deposant par id de commande (depuis l'historique 'Candidature').
  ipParCommande?: Record<string, string>;
  // Plis dechiffres : on remplace le bandeau "Plis scelles" par le bouton d'impression de l'offre.
  plisOuverts?: boolean;
  // Genere/telecharge le PDF de l'offre d'un candidat (cf. OperationAnalyse.handlePrintOffre).
  onPrintOffre?: (commandeId: number | string) => void;
}

const formatDateHeure = (value?: string | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${date} à ${heure}`;
};

// Statut de reponse derive de l'etat de la commande. Pour le refus, on nomme le refusant
// (commande.prestataire_signataire, resolu par core via id_user + type_compte du refus).
const getStatutReponse = (cmd?: CommandeAnalyse): { etat: string; ligne: string } => {
  if (!cmd) {
    return { etat: 'attente', ligne: 'En attente de réponse' };
  }
  const date = formatDateHeure(cmd.updated_at);
  switch (cmd.id_status) {
    case 5:
      return { etat: 'repondu', ligne: date ? `A répondu le ${date}` : 'A répondu' };
    case 6: {
      const dep = (cmd as { prestataire_signataire?: Deposant }).prestataire_signataire;
      const nom = dep ? `${dep.prenom || ''} ${dep.nom || ''}`.trim() : '';
      return {
        etat: 'refuse',
        ligne: `Consultation refusée${date ? ` le ${date}` : ''}${nom ? ` par ${nom}` : ''}`,
      };
    }
    case 12:
      return { etat: 'expire', ligne: 'Pas d\'offre reçue de ce candidat' };
    default:
      return { etat: 'attente', ligne: 'En attente de réponse' };
  }
};

const formatPhone = (phone?: string): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  let national = digits;
  if (digits.startsWith('33') && digits.length === 11) national = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) national = digits.slice(1);
  else return phone;
  return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
};

/**
 * Section 2 — Decryptage et ouverture des plis (vue acheteur MS, V2).
 * Une unite par prestataire : carte annuaire (gauche) + panneau d'offre (droite),
 * dans l'ordre des commandes (aligne avec la section "Analyse des offres"). Le fond est
 * grise tant que le prestataire n'a pas repondu (refus, expire, attente). Au clic sur la
 * carte : popup profil entreprise (meme modale que l'annuaire). Les plis restent scelles
 * (empreinte SHA256 placeholder).
 */
// Skeleton de chargement : reprend la structure de la carte annuaire (meme principe que la
// page Annuaire), affiche pendant le fetch de l'annuaire. Classes cs-sk* auto-contenues.
const OperationConsultationSuiviSkeleton: React.FC = () => (
  <div className="cs-unit cs-unit--skeleton" aria-hidden="true">
    <div className="prestataire-card prestataire-card--readonly cs-unit-card">
      <div className="prestataire-card-inner">
        <div className="prestataire-card-header">
          <span className="cs-sk cs-sk-banner" />
        </div>
        <div className="prestataire-card-logo-wrapper">
          <span className="cs-sk cs-sk-logo" />
        </div>
        <div className="prestataire-card-body">
          <span className="cs-sk cs-sk-line cs-sk-line--nom" />
          <span className="cs-sk cs-sk-line cs-sk-line--sm" />
          <span className="cs-sk cs-sk-line cs-sk-line--sm" />
        </div>
      </div>
    </div>
    <div className="cs-unit-offre">
      <span className="cs-sk cs-sk-line cs-sk-line--title" />
      <span className="cs-sk cs-sk-line" />
      <span className="cs-sk cs-sk-line cs-sk-line--sm" />
    </div>
  </div>
);

const OperationConsultationSuivi: React.FC<OperationConsultationSuiviProps> = ({
  commandes,
  lotTechnique,
  departement,
  ipParCommande,
  plisOuverts,
  onPrintOffre,
}) => {
  const [titulaires, setTitulaires] = useState<Titulaire[]>([]);
  const [loading, setLoading] = useState(false);
  const [annuaireKo, setAnnuaireKo] = useState(false);

  // Popup profil entreprise (reutilise la modale de l'annuaire ; fetch par entite.id).
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileContact, setProfileContact] = useState<ModalContact | null>(null);

  useEffect(() => {
    if (!lotTechnique || !departement) {
      setTitulaires([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setAnnuaireKo(false);

    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ departement: String(departement), lot: String(lotTechnique) });
    fetch(`${process.env.REACT_APP_API_URL}/prestataire/annuaire?${params}`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(r => r.json())
      .then((result: { data?: Titulaire[] }) => {
        if (cancelled) return;
        const contacts: Titulaire[] = result?.data ?? [];
        const parRang = ([1, 2, 3].map(r => contacts.find((c) => c.rang === r) ?? null).filter(Boolean)) as Titulaire[];
        setTitulaires(parRang.length ? parRang : contacts);
      })
      .catch(() => {
        if (!cancelled) {
          setTitulaires([]);
          setAnnuaireKo(true);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [lotTechnique, departement]);

  const openProfileModal = (entiteId: number, contact: ContactInfo | null) => {
    setProfile(null);
    setProfileError(null);
    setProfileLoading(true);
    setShowProfileModal(true);
    setProfileContact(contact
      ? {
          nom: contact.nom || '',
          prenom: contact.prenom || '',
          email: contact.email || contact.email_contact || '',
          telephone: contact.telephone || '',
          fonction: contact.fonction || '',
          contratNumero: null,
        }
      : null);

    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL}/annuaire/entites/${entiteId}/profil`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((r) => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then((d: { data?: ProfileData } & Partial<ProfileData>) => setProfile((d?.data ?? d) as ProfileData))
      .catch((err: Error) => setProfileError(err.message || 'Impossible de charger le profil.'))
      .finally(() => setProfileLoading(false));
  };

  // Index de l'annuaire par entite, pour enrichir chaque commande (logo, societe, interlocuteur).
  const titulaireParEntite = new Map<number, Titulaire>();
  titulaires.forEach((t) => {
    if (t.entite?.id != null) titulaireParEntite.set(Number(t.entite.id), t);
  });

  // On itere sur les COMMANDES (meme ordre que la section "Analyse des offres" en dessous).
  const cards: ConsultationCard[] = (commandes && commandes.length > 0)
    ? commandes.map((cmd, idx) => {
        const contrat = (cmd as { contrat?: { entite?: { id?: number; nom?: string; siret?: string | null }; lot_technique?: Titulaire['lot_technique'] } }).contrat;
        const eid = (cmd as { id_entite?: number }).id_entite ?? contrat?.entite?.id;
        const t = eid != null ? titulaireParEntite.get(Number(eid)) : undefined;
        return {
          key: cmd?.id ?? idx,
          entite: t?.entite
            ?? (contrat?.entite
              ? { id: contrat.entite.id, nom: contrat.entite.nom, siret: contrat.entite.siret, logo_url: null }
              : { id: (cmd as { id_entite?: number }).id_entite, nom: (cmd as { nom_entite?: string; nom?: string }).nom_entite || (cmd as { nom?: string }).nom || `Prestataire ${idx + 1}` }),
          lotTechnique: t?.lot_technique ?? contrat?.lot_technique,
          contact: t?.prestataire ?? (contrat?.entite as { interlocuteur?: ContactInfo } | undefined)?.interlocuteur ?? null,
          cmd,
        };
      })
    : titulaires.map((t, idx) => ({
        key: t.id ?? idx,
        entite: t.entite,
        lotTechnique: t.lot_technique,
        contact: t.prestataire || null,
        cmd: undefined,
      }));

  if (loading) {
    return (
      <div className="consultation-presta-grid">
        {Array.from({ length: 3 }).map((_, i) => <OperationConsultationSuiviSkeleton key={i} />)}
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <p>
        Les candidats et leurs offres apparaîtront dans cet onglet à l'issue du délai de consultation
        ou lorsque tous les candidats auront répondu.
      </p>
    );
  }

  return (
    <>
      {annuaireKo && (
        <p className="consultation-suivi-warn">
          Impossible de joindre l'annuaire : affichage simplifié des prestataires.
        </p>
      )}
      <div className="consultation-presta-grid">
        {cards.map((card) => {
          const statut = getStatutReponse(card.cmd);
          const societe = card.entite?.nom || 'Prestataire';
          const contact = card.contact;
          const contactNom = contact ? `${contact.prenom || ''} ${contact.nom || ''}`.trim() : '';
          const logoUrl = card.entite?.logo_url;
          const banner = card.lotTechnique?.image_url;

          const cmd = card.cmd;
          const dep = (cmd as { prestataire_signataire?: Deposant } | undefined)?.prestataire_signataire;
          const depNom = dep ? `${dep.prenom || ''} ${dep.nom || ''}`.trim() : '';
          const dateDepot = formatDateHeure(cmd?.updated_at);
          const numeroOffre = (cmd as { numero?: string | number } | undefined)?.numero ?? cmd?.id;
          const aRepondu = cmd?.id_status === 5;
          const ipDepot = cmd?.id != null ? ipParCommande?.[String(cmd.id)] : undefined;

          return (
            <div
              key={String(card.key)}
              className={`cs-unit cs-unit--${statut.etat}${aRepondu ? '' : ' cs-unit--grise'}`}
            >
              {/* Gauche : carte annuaire — seul element cliquable (ouvre le profil entreprise) */}
              <div
                className="prestataire-card prestataire-card--readonly cs-unit-card"
                role="button"
                onClick={() => { if (card.entite?.id != null) openProfileModal(Number(card.entite.id), contact); }}
              >
                <div className="prestataire-card-inner">
                  <div className="prestataire-card-header">
                    {banner ? (
                      <img src={banner} alt={card.lotTechnique?.nom || ''} className="prestataire-card-banner-img" />
                    ) : (
                      <div className="prestataire-card-banner-placeholder" style={{ backgroundColor: '#f97316' }} />
                    )}
                  </div>
                  <div className="prestataire-card-logo-wrapper">
                    {logoUrl ? (
                      <img src={logoUrl} alt={societe} className="prestataire-card-logo-img" />
                    ) : (
                      <span className="prestataire-card-logo-text">LOGO</span>
                    )}
                  </div>
                  <div className="prestataire-card-body">
                    <p className="prestataire-card-nom">{String(societe).toUpperCase()}</p>
                    {card.entite?.siret && (
                      <p className="lmm-card-info"><span className="lmm-card-info-label">SIRET</span>{card.entite.siret}</p>
                    )}
                    {contactNom && (
                      <p className="lmm-card-info lmm-card-info--contact">
                        {contactNom}{contact?.fonction ? ` – ${contact.fonction}` : ''}
                      </p>
                    )}
                    {contact?.email && (
                      <p className="lmm-card-info">
                        <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()}>{contact.email}</a>
                      </p>
                    )}
                    {contact?.telephone && (
                      <p className="lmm-card-info">{formatPhone(contact.telephone)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Droite : panneau offre */}
              <div className="cs-unit-offre">
                {aRepondu ? (
                  <>
                    <p className="cs-offre-titre">Offre N° {numeroOffre}</p>
                    {dateDepot && (
                      <p className="cs-line"><span className="cs-line-label">Offre déposée le</span>{dateDepot}</p>
                    )}
                    {depNom && (
                      <p className="cs-line">
                        <span className="cs-line-label">Par</span>
                        {depNom}{dep?.fonction ? ` – ${dep.fonction}` : ''}
                      </p>
                    )}
                    <p className={`cs-line${ipDepot ? '' : ' cs-line--muted'}`}>
                      <span className="cs-line-label">Adresse IP</span>{ipDepot || 'non capturée'}
                    </p>
                    {plisOuverts && onPrintOffre ? (
                      // Plis dechiffres : l'acheteur peut imprimer / telecharger l'offre du candidat.
                      <div className="cs-unit-actions" onClick={(e) => e.stopPropagation()}>
                        <Button
                          value="Offre du candidat"
                          icon={<PdfIcon />}
                          variant="secondary"
                          onClick={() => card.cmd?.id != null && onPrintOffre(card.cmd.id)}
                        />
                      </div>
                    ) : (
                      /* Plis encore scelles : empreinte placeholder (SVG inline, Font Awesome absent). */
                      <p className="cs-line cs-line--seal">
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
                          <path fill="currentColor" d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm3 8H9V6a3 3 0 0 1 6 0v3Z" />
                        </svg>
                        <span>Plis scellés — empreinte SHA256</span>
                      </p>
                    )}
                  </>
                ) : (
                  <p className={`cs-statut cs-statut--${statut.etat}`}>{statut.ligne}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ModalProfilMembre
        isOpen={showProfileModal}
        onClose={() => { setShowProfileModal(false); setProfile(null); setProfileError(null); setProfileContact(null); }}
        profile={profile}
        isLoading={profileLoading}
        error={profileError}
        contactInfo={profileContact}
        showCompletion={false}
        showPasserCommande={false}
      />
    </>
  );
};

export default OperationConsultationSuivi;
